import { MenuType } from './widget'

export type BrandTheme = 'light' | 'dark'
export type PrizeWheelDisplayPeriod = 'Days' | 'Weeks' | 'Months'
export type LandingVariant = 'modern' | 'linkTree'
export type LandingPageButtonLink = 'shopOnline' | 'directions' | 'callUs' | 'memberWallet'
export type IdentificationMethod = 'age-gate' | 'multiple-choice'
export type SignupFormFieldOptions =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'address'
  | 'dob'
  | 'favStore'
  | 'signature'
  | 'ageGate'

export interface PaddingType {
  top?: number
  right?: number
  bottom?: number
  left?: number
}

export interface BorderRadiusType {
  isSet?: boolean
  topLeft?: number
  topRight?: number
  bottomLeft?: number
  bottomRight?: number
}

export type BackgroundType = 'color' | 'image'

export interface ContainerStyles {
  display?: string
  flexDir?: string
  justifyContent?: string
  alignItems?: string
  textAlign?: string
  padding?: PaddingType
  margin?: PaddingType
  borderRadius?: BorderRadiusType
  bg?: string
  bgHover?: string
  borderColor?: string
  borderColorHover?: string
  backgroundImage?: string
  backgroundType?: BackgroundType
}

export interface TextStyles {
  color?: string
  colorHover?: string
  fontWeight?: string
  fontSize?: number
  fontFamily?: string
}

export interface TextContainer extends ContainerStyles, TextStyles {
  textDefault?: string
  textAlt?: string
  textSuccess?: string
  textLoading?: string
}

export interface InputStyle extends TextContainer {
  placeholder?: string
  selected?: TextContainer
  disabled?: TextContainer
}

export interface ButtonStyle extends TextContainer {
  height?: string
  width?: string
  selected?: TextContainer
  disabled?: TextContainer
}

export interface BrandColorPallet {
  brandColor?: string
  secondaryColor?: string
  specialColor?: string
  linkColor?: string
  successColor?: string
  warningColor?: string
  dangerColor?: string
  textBrand?: string
  textPrimary?: string
  textSecondary?: string
  surfacePrimary?: string
  surfaceSecondary?: string
  borderPrimary?: string
}

export interface BrandControlStyles {
  input?: InputStyle
  buttonPrimary?: ButtonStyle
  buttonSecondary?: ButtonStyle
  buttonSpecial?: ButtonStyle
}

export interface BrandStyle extends BrandColorPallet, TextStyles, BrandControlStyles {
  iconPresetID?: string // ID of the icon preset, or (classic, smooth, 8 bit, terpli)
}

export interface BrandLogos {
  logo?: string
  squareLogo?: string
  logoPlacement?: 'onCover' | 'belowCover'
  fullLogo?: string
  coverBackground?: string
  mmsLogo?: string
  optInLogo?: string
}

export interface AppleAppStoreInfo {
  appName?: string
  appSubtitle?: string
  appDescription?: string
  appKeywords?: string
  appIcon?: string
  appScreenshots?: string[]
  companyName?: string
  appleTeamID?: string
  appleAscID?: string
  appleBundleID?: string
}

export interface GooglePlayStoreInfo {
  appName?: string
  appSubtitle?: string
  appDescription?: string
  appKeywords?: string
  appIcon?: string
  appScreenshots?: string[]
  companyName?: string
  androidBundleID?: string
}

export interface AppStoreInfo {
  appleInfo?: AppleAppStoreInfo
  googleInfo?: GooglePlayStoreInfo
  termsAndConditionsUrl?: string
  privacyPolicyUrl?: string
  eulaUrl?: string
  consentText?: string
  slug?: string
  easID?: string
  lastBuildDate?: string
  lastVersion?: string
}

export interface AppImages extends BrandLogos {
  splashLoading?: string
  splashFallback?: string
}

export interface OrdersTabSettings {
  enabled?: boolean
  showOrderHistoryFeed?: boolean
}

export interface ShopTabSettings {
  enabled?: boolean
  shopUrl?: string
  enableAndroid?: boolean
  forceRedirect?: boolean
  forceRedirectInAppOnly?: boolean
}

export interface UpdatesTabSettings {
  enabled?: boolean
  showEmailsInFeed?: boolean
  hideMessagesBefore?: number
}

export interface DiscoverTabSettings {
  enabled?: boolean
  discoverUrl?: string
  appleDiscoverUrl?: string
  googleDiscoverUrl?: string
  enableAndroid?: boolean
  forceRedirect?: boolean
  forceRedirectInAppOnly?: boolean
}

export interface AppSignupField {
  enabled?: boolean
  required?: boolean
  field: string
  placeholder?: string
}

export interface AppFeaturesGeneral {
  signupFields?: Record<SignupFormFieldOptions, AppSignupField>
  forceSignupAfterLogin?: boolean
  browserNotificationOptIn?: boolean
  promptAddToHomeScreen?: boolean
  allowScanToPay?: boolean
}

export interface AppFeaturesStore {
  enableStoreDropdown?: boolean
  allowChangeStore?: boolean
  showDirections?: boolean
  showAddress?: boolean
  showCallStore?: boolean
}

export interface AppFeaturesSharing {
  showRaf?: boolean
  showReviews?: boolean
  showShareInMenu?: boolean
}

export interface AppFeaturesProductFeed {
  enabled?: boolean
  enableAndroid?: boolean
  showOnHomeTab?: boolean
  showOnShopTab?: boolean
  numberOfProducts?: number
}

export interface AppFeaturesOrdersFeed {
  enabled?: boolean
  numberOfOrders?: number
}

export interface AppFeaturesPointsAndDiscounts {
  enablePoints?: boolean
  enableDiscounts?: boolean
  showRedeemDiscounts?: boolean
  enableNativeDiscounts?: boolean
  showGiftCards?: boolean
}

export interface AppFeaturesProfile {
  showBirthday?: boolean
  showAddress?: boolean
}

export interface AppPrizeWheelFrequency {
  frequency?: number
  unit?: PrizeWheelDisplayPeriod
  start?: number
}

export interface AppPrizeWheelStyle {
  primaryColor?: string
  secondaryColor?: string
  notchColor?: string
}

export interface AppFeaturesPrizeWheel {
  enabled?: boolean
  wheelFrequency?: AppPrizeWheelFrequency
  style?: AppPrizeWheelStyle
}

export interface AppFeatures {
  general?: AppFeaturesGeneral
  store?: AppFeaturesStore
  sharing?: AppFeaturesSharing
  productFeed?: AppFeaturesProductFeed
  ordersFeed?: AppFeaturesOrdersFeed
  pointsAndDiscounts?: AppFeaturesPointsAndDiscounts
  profile?: AppFeaturesProfile
  prizeWheel?: AppFeaturesPrizeWheel
}

export interface AppFAQ {
  priority: number
  question: string
  answer: string
}

export interface AppTabsVerbiage {
  home?: string
  shop?: string
  orders?: string
  updates?: string
  discover?: string
}

export interface AppHamburgerMenuVerbiage {
  profileName?: string
  faqName?: string
  shareAppName?: string
  collectiblesName?: string
  scanToPayName?: string
  convertToGiftCardName?: string
  receiptScanName?: string
}

export interface AppNavigationVerbiage {
  tabs?: AppTabsVerbiage
  hamburgerMenu?: AppHamburgerMenuVerbiage
}

export interface GeneralVerbiage {
  points?: string
}

export interface AppHomeTabsVerbiage {
  recentPurchases?: string
  popularProducts?: string
}

export interface AppHomeVerbiage {
  tabs?: AppHomeTabsVerbiage
}

export interface AppShopTabsVerbiage {
  popularProducts?: string
  allProducts?: string
}

export interface AppShopVerbiage {
  tabs?: AppShopTabsVerbiage
}

export interface AppRAFVerbiage {
  header?: string
  description?: string
  shareText?: string
}

export interface AppReviewVerbiage {
  header?: string
  description?: string
}

export interface AppSocialVerbiage {
  raf?: AppRAFVerbiage
  review?: AppReviewVerbiage
}

export interface AppDiscountsVerbiage {
  redeemAtSalesCounterText?: string
  redeemInStoreText?: string
  redeemButtonText?: string
}

export interface AppVerbiage {
  general?: GeneralVerbiage
  navigation?: AppNavigationVerbiage
  home?: AppHomeVerbiage
  shop?: AppShopVerbiage
  social?: AppSocialVerbiage
  discounts?: AppDiscountsVerbiage
}

export interface LandingPageButtonLinkSettings {
  text?: string
  url?: string
  enabled?: boolean
}

export interface LandingPageSettings {
  variant?: LandingVariant
  captchaMethod?: IdentificationMethod
  buttonsMap?: Record<LandingPageButtonLink, LandingPageButtonLinkSettings>
  style?: BrandStyle
}

export interface CaptchaPageMethod {
  header?: TextContainer
  button?: ButtonStyle
  enabled?: boolean
}

export interface CaptchaPageMethods {
  pin?: CaptchaPageMethod
  dob?: CaptchaPageMethod
  phone?: CaptchaPageMethod
}

export interface CaptchaPageSettings {
  methods?: CaptchaPageMethods
  style?: BrandStyle
}

export interface AppSettings {
  version?: string
  appInfo?: AppStoreInfo
  submissionStatus?: 'Live' | 'In Review' | 'Archived / Churned'
  theme?: BrandTheme
  images?: AppImages
  style?: BrandStyle
  ordersTab?: OrdersTabSettings
  shopTab?: ShopTabSettings
  updatesTab?: UpdatesTabSettings
  discoverTab?: DiscoverTabSettings
  general?: AppFeaturesGeneral
  store?: AppFeaturesStore
  sharing?: AppFeaturesSharing
  productFeed?: AppFeaturesProductFeed
  ordersFeed?: AppFeaturesOrdersFeed
  pointsAndDiscounts?: AppFeaturesPointsAndDiscounts
  profile?: AppFeaturesProfile
  prizeWheel?: AppFeaturesPrizeWheel
  verbiage?: AppVerbiage
  faqs?: AppFAQ[]
  lastUpdated?: number
}

export type AttributionWindowUnitType = 'day' | 'hour'

export interface AttributionWindowDefaults {
  unit?: AttributionWindowUnitType
  count?: number
  actions?: string[]
}

export interface WalletPassAppleSettings {
  version?: string
  iconURL?: string
  logoURL?: string
  stripURL?: string
  foregroundColor?: string
  backgroundColor?: string
  labelColor?: string
  organizationName?: string
  logoText?: string
  description?: string
}

export interface WalletPassGoogleSettings {
  version?: string
  logoURL?: string
  heroImageURL?: string
  backgroundColor?: string
  textColor?: string
  organizationName?: string
  description?: string
}

export interface WalletPassDisplaySettings {
  showEverywhere?: boolean
  showInMobileApp?: boolean
  showInEcommerceMenu?: boolean
  showInEcommerceEmails?: boolean
  showInWidget?: boolean
}

export interface WalletPassSettings {
  enabled?: boolean
  apple?: WalletPassAppleSettings
  google?: WalletPassGoogleSettings
  displaySettings?: WalletPassDisplaySettings
}

export interface Range {
  min?: number
  max?: number
}

export interface WidgetScreen {
  title?: string
  description?: string
  loadingTitle?: string
  loadingDescription?: string
  textBlocks?: string[]
  hamburgerMenuTitle?: string
}

export interface WidgetScreenButtonSettings {
  primaryButtonText?: string
  secondaryButtonText?: string
  skipButtonText?: string
}

export interface WidgetProductDetailsScreenSettings extends WidgetScreen {
  buyAgainButtonText?: string
  findSimilarButtonText?: string
}

export interface WidgetSignInScreen extends WidgetScreen {
  primaryButtonText?: string
  secondaryButtonText?: string
  skipButtonText?: string
  infoBoxText?: string
  noPointsTitle?: string
  confirmTitle?: string
  confirmDescription?: string
  confirmButtonText?: string
  resendButtonText?: string
}

export interface WidgetReviewMyReviewsScreenSettings extends WidgetScreen {
  viewMoreButtonText?: string
  pendingReviewsTitle?: string
  completedReviewsTitle?: string
  pendingReviewsDescription?: string
  completedReviewsDescription?: string
}
export interface WidgetReviewCompleteScreenSettings extends WidgetScreen {
  pointsVerbiage?: string
}

export interface WidgetRecFlowScreen extends WidgetScreen {
  enabled?: boolean | null
  required?: boolean | null
  priority?: number
  edibleEnabled?: boolean | null
  edibleRequired?: boolean | null
  ediblePriority?: number
  edibleTitle?: string
  edibleDescription?: string
}

export interface WidgetReviewFlowScreen extends WidgetScreen {
  enabled?: boolean | null
  required?: boolean | null
  priority?: number
}

export interface WidgetTerpenesScreen extends WidgetRecFlowScreen {
  terpenesRange?: Range
  hideTerpeneEffects?: boolean
}

export interface WidgetChatScreen extends WidgetScreen {
  headerText?: string
  placeholderText?: string
  emptyStateText?: string
  initialSuggestions?: string[]
  sendButtonText?: string
  assistantName?: string
  loadingMessages?: string[]
}

export interface WidgetChatSettings {
  enabled?: boolean | null
  screenSettings?: {
    chat?: WidgetChatScreen
  }
}

export interface WidgetPriceScreen extends WidgetRecFlowScreen {
  priceRanges?: Range[]
}

export interface WidgetScreenSettings {
  welcome?: WidgetScreen
  signIn?: WidgetSignInScreen
  signUp?: WidgetScreen
  howItWorks?: WidgetScreen
  aboutTerpenes?: WidgetScreen
  productDetails?: WidgetScreen
}

export type RecommendationScreenSettings = {
  selectStore?: WidgetScreen
  selectMenuType?: WidgetScreen
  category?: WidgetScreen
  effects?: WidgetRecFlowScreen
  subCategory?: WidgetRecFlowScreen
  potency?: WidgetRecFlowScreen
  terpene?: WidgetTerpenesScreen
  price?: WidgetPriceScreen
  signIn?: WidgetSignInScreen
  walletPass?: WidgetScreen
  recommendations?: WidgetScreen
}

export type ReviewScreenKey = keyof ReviewScreenSettings

export type ReviewScreenSettings = {
  myReviews?: WidgetScreen
  signIn?: WidgetSignInScreen
  stars?: WidgetReviewFlowScreen
  effects?: WidgetReviewFlowScreen
  goodExperience?: WidgetReviewFlowScreen
  badExperience?: WidgetReviewFlowScreen
  potency?: WidgetReviewFlowScreen
  comment?: WidgetReviewFlowScreen
  claimPoints?: WidgetSignInScreen
  complete?: WidgetScreen
  productList?: WidgetScreen
}

export interface WidgetImages {
  loadingScreenImage?: string
  welcomeScreenImages?: Record<number, string>
}

export interface Effect {
  enabled?: boolean | null
  effect?: string
  direction?: string
  duration?: number
  delay?: number
  color?: string
  repeat?: boolean | null
  repeatCount?: number
  repeatDelay?: number
}

export interface WidgetLoyaltySettings {
  enabled?: boolean | null
  program?: 'AIQ' | 'SpringBig' | ''
  programName?: string
  signupFormID?: bigint // bigint in Zod might map to number or bigint in JS/TS, but sticking to bigint if possible, or number
  authFields?: ('email' | 'phone')[]
  firstRecPointsGift?: WidgetPointsGift
  // TODO(reviews): superseded by `reviewRewards.pointsPerReview`. Kept as a
  // fallback while merchant settings migrate to the new shape.
  reviewPointsGift?: WidgetReviewPointsGift
  reviewRewards?: WidgetReviewRewards
  walletPassEnabled?: boolean | null
  walletPassPointsGift?: WidgetPointsGift
}

export interface WidgetPointsGift {
  enabled?: boolean | null
  pointsGiftAmount?: number
}

export interface WidgetReviewPointsGift extends WidgetPointsGift {
  maxRedemptionsPerDay?: number
  isUnlimitedForPurchases?: boolean
}

export type WidgetReviewRewardType = 'pointsPerReview'

export interface WidgetReviewRewards {
  type?: WidgetReviewRewardType
  pointsPerReview?: WidgetPointsPerReviewRewardConfig
}

export interface WidgetPointsPerReviewRewardConfig {
  enabled?: boolean | null
  points?: number
  maxPerShopperPerDay?: number
  unlimited?: boolean | null
}

export interface WidgetIconDesign {
  enabled?: boolean | null
  mediaURL?: string
  height?: number
  width?: number
  position?: string
  borderRadius?: BorderRadiusType
  zIndex?: number
  effects?: Record<string, Effect>
  padding?: PaddingType
  displaySettings?: DisplaySettings
}

export interface WelcomeMessageDesign {
  enabled?: boolean
  useRichText?: boolean
  welcomeMessageType?: 'default' | 'custom'
  welcomeMessage?: string
  textStyles?: TextStyles
  textContainer?: TextContainer
  effects?: Record<string, Effect>
  displaySettings?: DisplaySettings
}

export type DynamicDisplayFrequencyUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'
export type DynamicDisplayTargetingCondition = 'equals' | 'notEquals' | 'contains' | 'notContains'

export interface DynamicDisplayTarget {
  id?: string
  value?: string
  condition?: DynamicDisplayTargetingCondition
}

export interface DynamicDisplayTrigger {
  enabled?: boolean
  count?: number
  unit?: DynamicDisplayFrequencyUnit
  threshold?: number
  actionDelay?: number
}

export interface DynamicDisplayTargets {
  includeURLs?: DynamicDisplayTarget[]
  excludeURLs?: DynamicDisplayTarget[]
}

export type DisplaySettingsTriggerType = 'onLoad' | 'onClick' | 'onScroll' | 'onExit'

export interface DisplaySettings {
  triggers?: Record<DisplaySettingsTriggerType, DynamicDisplayTrigger>
  displayFrequency?: DynamicDisplayTrigger
  targets?: DynamicDisplayTargets
}

export enum WidgetBreakpointTab {
  All = 'all',
  Desktop = 'desktop',
  Tablet = 'tablet',
  Phone = 'phone',
}

export interface WelcomeMessageSettings {
  enabled?: boolean | null
  welcomeMessageType?: 'default' | 'custom'
  welcomeMessage?: string
  displaySettings?: DisplaySettings
  designByBreakpoint?: Partial<Record<WidgetBreakpointTab, WelcomeMessageDesign>>
}
export interface RecommendationSettings {
  enabled?: boolean | null
  enableDeviceLocation?: boolean | null
  flowType?: RecommendationType
  screenSettings?: RecommendationScreenSettings
}

export enum RecommendationType {
  Default = 'default',
  Restricted = 'restricted',
}

export type WidgetLogoOption = 'full-logo' | 'avatar'

export interface WidgetPopupSettings {
  enabled?: boolean | null
  logoOption?: string
  enableLogo?: boolean | null
  enableCoverPhoto?: boolean | null
  headerText?: string
  headerSubext?: string
  bodyText?: string
  images?: BrandLogos
  style?: BrandStyle
  pageStyle?: ContainerStyles
  displaySettings?: DisplaySettings
}

export interface ReviewSettings {
  enabled: boolean | null
  reviewType: ReviewType
  screenSettings?: ReviewScreenSettings
}

export type ReviewType = 'clicked' | 'purchased'

export interface WidgetIconSettings {
  displaySettings?: DisplaySettings
  designByBreakpoint?: Partial<Record<WidgetBreakpointTab, WidgetIconDesign>>
}

export interface WidgetSettings {
  uid?: string
  storeID?: string
  globalID?: string
  menuType?: MenuType

  enabled?: boolean | null
  recommendationSettings?: RecommendationSettings
  reviewSettings?: ReviewSettings
  chatSettings?: WidgetChatSettings
  iconSettings?: WidgetIconSettings
  welcomeMessageSettings?: WelcomeMessageSettings
  popupSettings?: WidgetPopupSettings
  loyaltySettings?: WidgetLoyaltySettings
  screenSettings?: WidgetScreenSettings
  images?: WidgetImages
  brandImages?: BrandLogos
  style?: BrandStyle
  footerSettings?: WidgetFooterSettings
}

export interface WidgetFooterSettings {
  enabled?: boolean | undefined
  logoSrc?: string
  height?: number
  width?: number
}

export interface UserSettings {
  uid?: string
  storeID?: string
  globalID?: string
  version?: number
  images?: BrandLogos
  style?: BrandStyle
  appSettings?: AppSettings
  landingPage?: LandingPageSettings
  captchaPage?: CaptchaPageSettings
  merged?: boolean
}

export type DisplaySettingsTriggerConfig = {
  label: string
  short: string
  tooltip: string
  showThreshold: boolean
  showDelay: boolean
  thresholdSuffix: string
  thresholdPrefix: string
  actionDelaySuffix: string
  actionDelayPrefix: string
  thresholdTooltip: string
  actionDelayTooltip: string
}

export const DisplaySettingsTriggerMap: Record<DisplaySettingsTriggerType, DisplaySettingsTriggerConfig> = {
  onLoad: {
    tooltip: 'When set the popup will display when the page loads',
    label: 'Show on page load',
    short: 'load',
    showThreshold: false,
    showDelay: true,
    thresholdSuffix: '',
    thresholdPrefix: '',
    actionDelayPrefix: 'Show',
    actionDelaySuffix: 'seconds after page load',
    thresholdTooltip: '',
    actionDelayTooltip: 'When set the popup will not display until x seconds after page load',
  },
  onClick: {
    tooltip: 'When set the popup will display when the user clicks anywhere on the page',
    label: 'Show on click',
    short: 'click',
    showThreshold: true,
    showDelay: true,
    thresholdSuffix: 'clicks',
    thresholdPrefix: 'Show after',
    actionDelayPrefix: 'Show',
    actionDelaySuffix: 'seconds after click',
    thresholdTooltip: 'When set the popup will not display until the user has clicked x times',
    actionDelayTooltip: 'When set the popup will not display until x seconds after click',
  },
  onScroll: {
    tooltip: 'When set the popup will display when the user scrolls the page',
    label: 'Show on scroll',
    short: 'scroll',
    showThreshold: false,
    showDelay: true,
    thresholdPrefix: 'Show after scrolling',
    thresholdSuffix: '% of the page',
    actionDelayPrefix: 'Show',
    actionDelaySuffix: 'seconds after scroll',
    thresholdTooltip: 'When set the popup will not display until the user has scrolled x% of the page',
    actionDelayTooltip: 'When set the popup will not display until x seconds after scrolling',
  },
  onExit: {
    tooltip: 'When set the popup will display when the user is about to exit the page',
    label: 'Show on exit',
    short: 'exit',
    showThreshold: false,
    showDelay: false,
    thresholdSuffix: '',
    thresholdPrefix: '',
    actionDelayPrefix: 'Show',
    actionDelaySuffix: 'seconds after exit',
    thresholdTooltip: '',
    actionDelayTooltip: 'When set the popup will not display until x seconds after exit',
  },
}

export type BaseOverrides = {
  brandColor?: string
  dangerColor?: string
  successColor?: string
  textPrimaryColor?: string
  textSecondaryColor?: string
  borderColor?: string
  borderRadius?: string
}
